import './ResultsGrid.scss';

import * as React from 'react';

import { ActivityStage } from '@showbie-socrative/socrative-utils';

import { translate } from '~l10n/client';
import { Divider } from '~shared/components/Divider';
import { Inline } from '~shared/components/Inline';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { ResultsStatsRow } from '~teacher/components/ResultsGrid/ResultsStatsRow';

import {
  useScoreDisplay,
  useShowNames,
  useShowResponses,
  useShowResults,
} from './atoms';
import {
  Participant,
  ResultsTable,
  ResultsTableQuestion,
} from './ResultsTable';

interface Props {
  resultsTableQuestions?: ResultsTableQuestion[];
  participants?: Participant[];
  maxScore: number;
  activityInstanceId: number;
  activityStage: ActivityStage;
  isRosteredRoom: boolean;
  passRate: number | null;
  totalStudents?: number | null;
  presentStudents?: number;
  completionRate?: number;
  averageScoreRate?: number | null;
  passingStudentsRate?: number;
}

/**
 * Controls layout of Results view options and Results table.
 */
export function ResultsGrid({
  resultsTableQuestions,
  participants,
  maxScore,
  activityInstanceId,
  activityStage,
  isRosteredRoom,
  passRate,
  totalStudents,
  presentStudents,
  completionRate,
  averageScoreRate,
  passingStudentsRate,
}: Props): React.ReactElement {
  const isActivityInProgress = activityStage === ActivityStage.InProgress;

  const [showNames, setShowNames] = useShowNames(isActivityInProgress);
  const [showResponses, setShowResponses] =
    useShowResponses(isActivityInProgress);
  const [showResults, setShowResults] = useShowResults(isActivityInProgress);

  const [scoreDisplay, setScoreDisplay] = useScoreDisplay(isActivityInProgress);

  const gridOptionsRef = React.useRef<HTMLDivElement>(null);

  return (
    <div data-testid="results-grid" className="pb-12 results-grid">
      <div
        className="sticky top-0 left-0 z-30 -mr-8 bg-white w-screen legacy-container"
        ref={gridOptionsRef}
      >
        {!isActivityInProgress && (
          <ResultsStatsRow
            activityInstanceId={activityInstanceId}
            passRate={passRate}
            totalStudents={totalStudents}
            presentStudents={presentStudents}
            completionRate={completionRate}
            averageScoreRate={averageScoreRate}
            passingStudentsRate={passingStudentsRate}
          />
        )}
        <div className="pt-6 pb-2 bg-white">
          <div className="flex flex-col gap-4 md:flex md:flex-row">
            <label className="flex items-center cursor-pointer">
              <ToggleSwitch
                size="small"
                on={showNames}
                onChange={(checked) => {
                  setShowNames(checked);
                }}
              />
              <span
                className="pl-2 text-sm font-500 text-blue-900"
                data-testid="show-names"
              >
                {translate('Show Names')}
              </span>
            </label>
            <label className="flex items-center md:pl-3 cursor-pointer">
              <ToggleSwitch
                size="small"
                on={showResponses}
                onChange={(checked) => {
                  setShowResponses(checked);
                }}
              />
              <span
                className="pl-2 text-sm font-500 text-blue-900"
                data-testid="show-responses"
              >
                {translate('Show Responses')}
              </span>
            </label>
            <label className="flex items-center md:pl-3 cursor-pointer">
              <ToggleSwitch
                size="small"
                on={showResults}
                onChange={(checked) => {
                  setShowResults(checked);
                }}
              />
              <span
                className="pl-2 text-sm font-500 text-blue-900"
                data-testid="show-results"
              >
                {translate('Show Results')}
              </span>
            </label> 
          </div>
        </div>
      </div>
      <ResultsTable
        isRosteredRoom={isRosteredRoom}
        isActivityInProgress={isActivityInProgress}
        showNames={showNames}
        showResults={showResults}
        showResponses={showResponses}
        scoreDisplay={scoreDisplay}
        setScoreDisplay={setScoreDisplay}
        resultsTableQuestions={resultsTableQuestions}
        participants={participants}
        maxScore={maxScore}
        activityInstanceId={activityInstanceId}
        optionsContainer={gridOptionsRef}
      />
    </div>
  );
}
